import { useEffect, useReducer, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/* ============================================================
   BuildPipelineMockup — 4-stage hero animation
   01 · Wireframe → hand-dashed grid + annotated blocks draw on
   02 · Code      → giant IDE; existing file dimmed at top,
                    new code typed line-by-line at the bottom,
                    line numbers in the thousands (0247…)
   03 · Render    → ex-nihilo-paris editorial finish:
                    serif numerals, generous whitespace,
                    full-bleed product image, side metadata
   04 · Convert   → "Add to bag" pulses, refined cart drawer
                    slides in, "Order placed" flourish
   Loops indefinitely. Pauses when off-screen or
   prefers-reduced-motion is set.
============================================================ */

const STAGE_DURATIONS = {
  wireframe: 3200,
  code: 5800,
  render: 4400,
  convert: 4200,
};
const STAGES = ["wireframe", "code", "render", "convert"];

/* ============================================================
   Stage 01 · Wireframe
============================================================ */
const WireframeStage = () => {
  const draw = (delay, duration = 0.7) => ({
    initial: { pathLength: 0, opacity: 0 },
    animate: { pathLength: 1, opacity: 1 },
    transition: { duration, delay, ease: [0.22, 1, 0.36, 1] },
  });
  const fade = (delay, y = 0) => ({
    initial: { opacity: 0, y },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] },
  });
  return (
    <div className="absolute inset-0 bg-[#FAF8F3] overflow-hidden">
      {/* Hand-dashed grid */}
      <span
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.55]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(11,15,20,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(11,15,20,0.06) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      />

      {/* Browser dummy */}
      <div className="flex items-center gap-1.5 h-7 px-3 border-b border-black/[0.06]">
        <span className="w-1.5 h-1.5 rounded-full bg-black/20" />
        <span className="w-1.5 h-1.5 rounded-full bg-black/20" />
        <span className="w-1.5 h-1.5 rounded-full bg-black/20" />
        <span className="ml-3 text-[0.55rem] font-mono text-neutral-500 tracking-wider">
          /products/object-no-01 — wireframe
        </span>
      </div>

      <div className="relative px-6 pt-6 pb-5 h-[calc(100%-1.75rem)]">
        <svg viewBox="0 0 320 360" preserveAspectRatio="none" className="w-full h-full">
          {/* Image grid block */}
          <motion.rect
            x="14" y="14" width="200" height="200" rx="6"
            stroke="#0B0F14" strokeWidth="1.5" fill="none" strokeDasharray="3 3"
            {...draw(0.05)}
          />
          {/* Diagonal cross to suggest "image goes here" */}
          <motion.path
            d="M 14 14 L 214 214 M 214 14 L 14 214"
            stroke="#0B0F14" strokeWidth="0.6" strokeDasharray="2 4" fill="none" opacity="0.35"
            {...draw(0.45, 0.5)}
          />
          {/* Thumb stack */}
          {[0, 1, 2].map((i) => (
            <motion.rect
              key={i}
              x="222" y={14 + i * 64} width="56" height="56" rx="4"
              stroke="#0B0F14" strokeWidth="1.5" fill="none" strokeDasharray="3 3"
              {...draw(0.18 + i * 0.08)}
            />
          ))}
          {/* Title placeholder */}
          <motion.rect x="14" y="232" width="180" height="14" rx="3" fill="#0B0F14" {...fade(0.55)} />
          {/* Subtitle */}
          <motion.rect x="14" y="254" width="120" height="9" rx="3" fill="#0B0F14" opacity="0.4" {...fade(0.7)} />
          {/* Price */}
          <motion.rect x="14" y="280" width="60" height="11" rx="3" fill="#0B0F14" opacity="0.7" {...fade(0.85)} />
          {/* CTA */}
          <motion.rect
            x="14" y="306" width="290" height="34" rx="4"
            stroke="#0B0F14" strokeWidth="1.5" fill="none" strokeDasharray="4 4"
            {...draw(1.0)}
          />
          <motion.text
            x="159" y="328" textAnchor="middle"
            fill="#0B0F14" fontSize="9" fontWeight="700" letterSpacing="2"
            {...fade(1.25)}
          >
            ADD TO BAG
          </motion.text>
          {/* Annotation arrow + label */}
          <motion.path
            d="M 240 270 C 230 286 220 300 200 318"
            stroke="#F4D64E" strokeWidth="1.5" strokeDasharray="3 3" fill="none"
            {...draw(1.0)}
          />
          <motion.text
            x="248" y="262" fill="#0B0F14" fontSize="6.5" fontWeight="700" letterSpacing="1.4"
            {...fade(1.35)}
          >
            CTA · primary
          </motion.text>
        </svg>

        {/* Hand-written tag */}
        <motion.div
          {...fade(1.3, 6)}
          className="absolute top-3 right-4 text-[0.55rem] font-bold tracking-[0.22em] uppercase text-[#0B0F14] bg-[#F4D64E] px-2 py-1 rounded"
        >
          v1.0 wireframe
        </motion.div>
      </div>
    </div>
  );
};

/* ============================================================
   Stage 02 · Code — full-screen IDE.
   Lines 0001-0235 are pre-written and dimmed; new lines (0236+)
   are typed on, character-by-character, into the visible viewport
   that auto-scrolls with each new line.
============================================================ */

/* Color tokens for syntax-highlight */
const TOKEN_COLORS = {
  punc: "text-neutral-500",
  kw: "text-[#FF7AB6]",
  str: "text-[#F4D64E]",
  tag: "text-[#82E1FF]",
  attr: "text-[#A1F0C4]",
  mu: "text-[#F4D64E]",
  var: "text-white",
  comp: "text-[#C8A8FF]",
  fn: "text-[#82E1FF]",
  cmt: "text-neutral-500 italic",
  num: "text-[#F4D64E]",
  op: "text-[#FF7AB6]",
};

/* Pre-written body — already in the file when the camera arrives.
   Rendered dim so it reads as "context above". */
const PRE_LINES = [
  [{ t: '/* ============================================================', c: "cmt" }],
  [{ t: '   product-pdp.liquid · v3.4', c: "cmt" }],
  [{ t: '   Sunday Digital · sundaydigital.io', c: "cmt" }],
  [{ t: '   built for Object Nº01 · Series 03', c: "cmt" }],
  [{ t: '============================================================ */', c: "cmt" }],
  [{ t: ' ', c: "punc" }],
  [{ t: "{% ", c: "punc" }, { t: "section ", c: "kw" }, { t: '"announcement-bar"', c: "str" }, { t: " %}", c: "punc" }],
  [{ t: "{% ", c: "punc" }, { t: "section ", c: "kw" }, { t: '"navigation"', c: "str" }, { t: " %}", c: "punc" }],
  [{ t: " ", c: "punc" }],
  [{ t: "<", c: "punc" }, { t: "section", c: "tag" }, { t: " class=", c: "attr" }, { t: '"pdp pdp--editorial"', c: "str" }, { t: " data-id=", c: "attr" }, { t: "{{", c: "mu" }, { t: " product.id ", c: "var" }, { t: "}}", c: "mu" }, { t: ">", c: "punc" }],
  [{ t: "  <", c: "punc" }, { t: "Gallery", c: "comp" }, { t: " media=", c: "attr" }, { t: "{ ", c: "mu" }, { t: "product.media", c: "var" }, { t: " }", c: "mu" }, { t: " variant=", c: "attr" }, { t: '"editorial-stack"', c: "str" }, { t: " />", c: "punc" }],
  [{ t: "  <", c: "punc" }, { t: "h1", c: "tag" }, { t: " class=", c: "attr" }, { t: '"pdp__title"', c: "str" }, { t: ">", c: "punc" }, { t: "{{", c: "mu" }, { t: " product.title ", c: "var" }, { t: "}}", c: "mu" }, { t: "</", c: "punc" }, { t: "h1", c: "tag" }, { t: ">", c: "punc" }],
  [{ t: "  <", c: "punc" }, { t: "Price", c: "comp" }, { t: " value=", c: "attr" }, { t: "{ ", c: "mu" }, { t: "variant.price", c: "var" }, { t: " }", c: "mu" }, { t: " currency=", c: "attr" }, { t: '"USD"', c: "str" }, { t: " />", c: "punc" }],
  [{ t: "  <", c: "punc" }, { t: "VariantPicker", c: "comp" }, { t: " options=", c: "attr" }, { t: "{ ", c: "mu" }, { t: "product.options", c: "var" }, { t: " }", c: "mu" }, { t: " />", c: "punc" }],
  [{ t: "  <", c: "punc" }, { t: "AddToCart", c: "comp" }, { t: " variant=", c: "attr" }, { t: "{ ", c: "mu" }, { t: "variant.id", c: "var" }, { t: " }", c: "mu" }, { t: " onAdd=", c: "attr" }, { t: "{ ", c: "mu" }, { t: "openDrawer", c: "fn" }, { t: " }", c: "mu" }, { t: " />", c: "punc" }],
  [{ t: "</", c: "punc" }, { t: "section", c: "tag" }, { t: ">", c: "punc" }],
  [{ t: " ", c: "punc" }],
  [{ t: "// ", c: "cmt" }, { t: "—— cart drawer · sunday/cart-drawer.js · v2.1.0 ——", c: "cmt" }],
  [{ t: "import ", c: "kw" }, { t: "{ ", c: "punc" }, { t: "fetchCart", c: "fn" }, { t: ", ", c: "punc" }, { t: "renderLine", c: "fn" }, { t: " } ", c: "punc" }, { t: "from ", c: "kw" }, { t: '"./cart"', c: "str" }, { t: ";", c: "punc" }],
  [{ t: "import ", c: "kw" }, { t: "{ ", c: "punc" }, { t: "track", c: "fn" }, { t: " } ", c: "punc" }, { t: "from ", c: "kw" }, { t: '"./analytics"', c: "str" }, { t: ";", c: "punc" }],
  [{ t: " ", c: "punc" }],
  [{ t: "const ", c: "kw" }, { t: "TRANSITION", c: "var" }, { t: " = ", c: "punc" }, { t: "120", c: "num" }, { t: ";", c: "punc" }, { t: "  // ms — must beat 200ms perception bar", c: "cmt" }],
  [{ t: " ", c: "punc" }],
];

/* The "live" portion typed on as the camera holds.
   Each line lands one-by-one while the viewport scrolls so the
   newest line stays visible at the bottom. */
const LIVE_LINES = [
  [{ t: "export ", c: "kw" }, { t: "async function", c: "kw" }, { t: " ", c: "punc" }, { t: "addToCart", c: "fn" }, { t: "(", c: "punc" }, { t: "id", c: "var" }, { t: ", ", c: "punc" }, { t: "qty", c: "var" }, { t: " = ", c: "op" }, { t: "1", c: "num" }, { t: ") {", c: "punc" }],
  [{ t: "  const ", c: "kw" }, { t: "started", c: "var" }, { t: " = ", c: "op" }, { t: "performance", c: "fn" }, { t: ".", c: "punc" }, { t: "now", c: "fn" }, { t: "();", c: "punc" }],
  [{ t: "  const ", c: "kw" }, { t: "res", c: "var" }, { t: " = ", c: "op" }, { t: "await ", c: "kw" }, { t: "fetch", c: "fn" }, { t: "(", c: "punc" }, { t: '"/cart/add.js"', c: "str" }, { t: ", { ", c: "punc" }],
  [{ t: "    method", c: "attr" }, { t: ": ", c: "punc" }, { t: '"POST"', c: "str" }, { t: ",", c: "punc" }],
  [{ t: "    headers", c: "attr" }, { t: ": { ", c: "punc" }, { t: '"Content-Type"', c: "str" }, { t: ": ", c: "punc" }, { t: '"application/json"', c: "str" }, { t: " },", c: "punc" }],
  [{ t: "    body", c: "attr" }, { t: ": ", c: "punc" }, { t: "JSON", c: "fn" }, { t: ".", c: "punc" }, { t: "stringify", c: "fn" }, { t: "({ ", c: "punc" }, { t: "id", c: "attr" }, { t: ", ", c: "punc" }, { t: "quantity", c: "attr" }, { t: ": ", c: "punc" }, { t: "qty", c: "var" }, { t: " })", c: "punc" }],
  [{ t: "  });", c: "punc" }],
  [{ t: "  const ", c: "kw" }, { t: "line", c: "var" }, { t: " = ", c: "op" }, { t: "await ", c: "kw" }, { t: "res", c: "var" }, { t: ".", c: "punc" }, { t: "json", c: "fn" }, { t: "();", c: "punc" }],
  [{ t: "  ", c: "punc" }, { t: "track", c: "fn" }, { t: "(", c: "punc" }, { t: '"cart_added"', c: "str" }, { t: ", { ", c: "punc" }, { t: "id", c: "attr" }, { t: ", ", c: "punc" }, { t: "ms", c: "attr" }, { t: ": ", c: "punc" }, { t: "performance", c: "fn" }, { t: ".", c: "punc" }, { t: "now", c: "fn" }, { t: "() ", c: "punc" }, { t: "- ", c: "op" }, { t: "started", c: "var" }, { t: " });", c: "punc" }],
  [{ t: "  ", c: "punc" }, { t: "renderLine", c: "fn" }, { t: "(", c: "punc" }, { t: "line", c: "var" }, { t: ");", c: "punc" }],
  [{ t: "  return ", c: "kw" }, { t: "openDrawer", c: "fn" }, { t: "({ ", c: "punc" }, { t: "duration", c: "attr" }, { t: ": ", c: "punc" }, { t: "TRANSITION", c: "var" }, { t: " });", c: "punc" }],
  [{ t: "}", c: "punc" }],
];

/* Hook: types LIVE_LINES on, one line at a time, character-by-character.
   `typedLines` is a partial mirror of LIVE_LINES — for each entry, we
   either return the full line or a tuple (lineIndex, charsRevealed). */
const useTypewriter = (active) => {
  const [progress, setProgress] = useState({ line: 0, char: 0 });
  useEffect(() => {
    if (!active) {
      setProgress({ line: 0, char: 0 });
      return;
    }
    let cancelled = false;
    let timer;
    const step = () => {
      if (cancelled) return;
      setProgress((p) => {
        const total = LIVE_LINES.length;
        if (p.line >= total) return p;
        const lineTokens = LIVE_LINES[p.line];
        const lineChars = lineTokens.reduce((n, t) => n + t.t.length, 0);
        if (p.char >= lineChars) {
          // jump to next line
          return { line: p.line + 1, char: 0 };
        }
        // reveal a chunk to keep things lively (3-6 chars per tick)
        const chunk = Math.max(2, Math.floor(Math.random() * 5));
        return { line: p.line, char: Math.min(p.char + chunk, lineChars) };
      });
      timer = setTimeout(step, 24);
    };
    timer = setTimeout(step, 350); // small lead-in
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [active]);
  return progress;
};

const renderRevealed = (lineTokens, charsRevealed) => {
  let remaining = charsRevealed;
  const out = [];
  for (let i = 0; i < lineTokens.length; i++) {
    const tok = lineTokens[i];
    if (remaining <= 0) break;
    const slice = tok.t.slice(0, remaining);
    out.push(
      <span key={i} className={TOKEN_COLORS[tok.c] || "text-white"}>
        {slice}
      </span>
    );
    remaining -= tok.t.length;
  }
  return out;
};

const PRE_LINE_NUMBER_START = 215; // file appears to be deep in (line 215+)

const CodeStage = ({ active }) => {
  const progress = useTypewriter(active);
  const totalRendered = PRE_LINES.length + progress.line + (progress.char > 0 ? 1 : 0);
  const ln = (i) => String(PRE_LINE_NUMBER_START + i).padStart(4, "0");

  // Auto-scroll: keep newest line in view by scrolling the inner viewport.
  const bodyRef = useRef(null);
  useEffect(() => {
    if (!bodyRef.current) return;
    bodyRef.current.scrollTo({
      top: bodyRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [progress.line, progress.char, totalRendered]);

  return (
    <div className="absolute inset-0 bg-[#0B0F14] overflow-hidden">
      {/* IDE chrome */}
      <div className="flex items-center gap-2 px-4 h-9 border-b border-white/[0.06] bg-[#0F141A]">
        <span className="w-2 h-2 rounded-full bg-[#FF5F57]" />
        <span className="w-2 h-2 rounded-full bg-[#FEBC2E]" />
        <span className="w-2 h-2 rounded-full bg-[#28C840]" />
        <div className="ml-3 flex items-center gap-1.5 text-[0.6rem] font-mono">
          <span className="px-2.5 py-0.5 rounded-t-md bg-[#0B0F14] border-t border-l border-r border-white/[0.08] text-neutral-200 inline-flex items-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-[#F4D64E]" />
            cart-drawer.js
          </span>
          <span className="px-2.5 py-0.5 rounded-t-md text-neutral-500">
            product-pdp.liquid
          </span>
          <span className="px-2.5 py-0.5 rounded-t-md text-neutral-500">
            theme.css
          </span>
        </div>
        <span className="ml-auto text-[0.55rem] font-mono text-neutral-500 tracking-wider">
          UTF-8 · LF · main ●
        </span>
      </div>

      {/* Code body — auto-scroll viewport */}
      <div
        ref={bodyRef}
        className="flex h-[calc(100%-2.25rem-1.4rem)] font-mono text-[0.7rem] leading-[1.55] overflow-hidden"
      >
        {/* Gutter */}
        <div className="shrink-0 w-12 text-right pr-3 pt-3 select-none text-[0.62rem]">
          {PRE_LINES.map((_, i) => (
            <div key={`pre-${i}`} className="text-neutral-700/60 tabular-nums">
              {ln(i)}
            </div>
          ))}
          {LIVE_LINES.slice(0, progress.line + 1).map((_, i) => (
            <div
              key={`live-${i}`}
              className={`tabular-nums ${
                i === progress.line ? "text-[#F4D64E]" : "text-neutral-500"
              }`}
            >
              {ln(PRE_LINES.length + i)}
            </div>
          ))}
        </div>

        {/* Source */}
        <div className="flex-1 pt-3 pr-4">
          {/* Pre-written context — dimmed */}
          {PRE_LINES.map((line, i) => (
            <div key={`pre-${i}`} className="whitespace-pre opacity-40">
              {line.map((tok, j) => (
                <span key={j} className={TOKEN_COLORS[tok.c] || "text-white"}>
                  {tok.t}
                </span>
              ))}
            </div>
          ))}

          {/* Already-completed live lines */}
          {LIVE_LINES.slice(0, progress.line).map((line, i) => (
            <div key={`done-${i}`} className="whitespace-pre">
              {line.map((tok, j) => (
                <span key={j} className={TOKEN_COLORS[tok.c] || "text-white"}>
                  {tok.t}
                </span>
              ))}
            </div>
          ))}

          {/* Currently-typing line — only render once we've started it */}
          {progress.line < LIVE_LINES.length ? (
            <div className="whitespace-pre relative">
              {/* subtle highlight on the active line */}
              <span
                aria-hidden
                className="absolute -inset-x-2 inset-y-0 bg-[#F4D64E]/[0.04] pointer-events-none rounded-sm"
              />
              {renderRevealed(LIVE_LINES[progress.line], progress.char)}
              <motion.span
                className="inline-block w-[7px] h-[12px] ml-px bg-[#F4D64E] align-baseline translate-y-[1px]"
                animate={{ opacity: [1, 0, 1] }}
                transition={{ duration: 0.85, repeat: Infinity, ease: "linear" }}
              />
            </div>
          ) : null}
        </div>
      </div>

      {/* Status bar */}
      <div className="absolute bottom-0 left-0 right-0 h-[1.4rem] flex items-center px-3 bg-[#0F141A] border-t border-white/[0.06] text-[0.55rem] font-mono tracking-wider text-neutral-500">
        <span className="inline-flex items-center gap-1.5 text-[#F4D64E]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#F4D64E] animate-pulse" />
          {progress.line >= LIVE_LINES.length ? "✓ Compiled · 86ms" : "▶ Building…"}
        </span>
        <span className="ml-4">
          Ln {String(PRE_LINE_NUMBER_START + PRE_LINES.length + progress.line).padStart(4, "0")}, Col {String(Math.max(1, progress.char)).padStart(2, "0")}
        </span>
        <span className="ml-auto">{LIVE_LINES.length === 0 ? "" : `${Math.min(progress.line, LIVE_LINES.length)} / ${LIVE_LINES.length} fns`}</span>
      </div>

      {/* Compile glow at end */}
      <motion.div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: progress.line >= LIVE_LINES.length ? 0.45 : 0 }}
        transition={{ duration: 0.5 }}
        style={{
          background:
            "radial-gradient(600px 400px at 50% 60%, rgba(244,214,78,0.20), transparent 70%)",
        }}
      />
    </div>
  );
};

/* ============================================================
   Stage 03 · Render — ex-nihilo-paris editorial finish
============================================================ */
/* ============================================================
   Stage 03 · Render — full luxury Shopify storefront
   (inspired by editorial brand films: Atelier-style homepage on a
   wide canvas, refined header / hero / product grid / brand mark)
   Stage 04 · Convert — same storefront stays visible while a PDP
   peek-overlay slides up, "Add to bag" pulses, cart drawer slides
   in, and a serif "Order placed." flourish closes the loop.
============================================================ */
const STORE = {
  brand: "MAISON SUNDAY",
  eyebrow: "Édition III · Spring 26",
  hero: {
    img: "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=2400&q=80",
    title_a: "Made",
    title_b: "to outlast",
    title_c: "the season.",
  },
  collection: [
    {
      label: "Atelier",
      name: "Coat Nº01",
      img: "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=1200&q=80",
    },
    {
      label: "Leather",
      name: "Bag Nº07",
      img: "https://images.unsplash.com/photo-1591561954557-26941169b49e?auto=format&fit=crop&w=1200&q=80",
    },
    {
      label: "Studio",
      name: "Object Nº01",
      img: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&w=1200&q=80",
    },
  ],
  // Editorial story section appears below the collection grid
  // when the storefront scrolls during the Render stage.
  story: {
    eyebrow: "The Atelier · Paris",
    title_a: "Slow craft,",
    title_b: "kept honest.",
    body:
      "Each Édition III piece is patterned, cut, and finished by hand in our 11ème workshop — a slow, deliberate practice in a fast world.",
    img: "https://images.unsplash.com/photo-1558769132-cb1aea458c5e?auto=format&fit=crop&w=1400&q=80",
    cta: "Read the journal",
  },
  press: ["VOGUE", "WALLPAPER*", "THE CUT", "ELLE", "AD", "MR PORTER"],
  pdp: {
    badge: "Featured · Atelier",
    series: "Édition III · Limited 120",
    name: "Coat Nº01",
    price: "€ 1 480",
    img: "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=2000&q=80",
  },
  // Cart line items shown in the rich Shopify-style drawer
  cart: {
    items: [
      {
        name: "Coat Nº01",
        meta: "Atelier · Size S",
        price: "€ 1 480",
        qty: 1,
        img: "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=600&q=80",
      },
      {
        name: "Cashmere scarf",
        meta: "Studio · One size",
        price: "€ 180",
        qty: 1,
        img: "https://images.unsplash.com/photo-1601925268897-2da11dfeb38a?auto=format&fit=crop&w=600&q=80",
      },
    ],
    upsell: [
      {
        name: "Bag Nº07",
        price: "€ 620",
        img: "https://images.unsplash.com/photo-1591561954557-26941169b49e?auto=format&fit=crop&w=600&q=80",
      },
      {
        name: "Leather card-holder",
        price: "€ 140",
        img: "https://images.unsplash.com/photo-1606760227091-3dd870d97f1d?auto=format&fit=crop&w=600&q=80",
      },
    ],
    subtotal: "€ 1 660",
    discount_label: "SPRING10",
    discount_amount: "− € 166",
    total: "€ 1 494",
    free_shipping_threshold: "€ 200",
    free_shipping_unlocked: true,
    stock_alert: "Only 3 left in your size",
  },
};

const RenderStage = ({ pulseCta = false, drawerOpen = false, success = false, focusPdp = false }) => (
  <div className="absolute inset-0 bg-[#F5F2EC] overflow-hidden">
    {/* Browser */}
    <div className="flex items-center gap-1.5 h-7 px-3 border-b border-black/[0.05] bg-[#EFEBE3]">
      <span className="w-1.5 h-1.5 rounded-full bg-[#FF5F57]" />
      <span className="w-1.5 h-1.5 rounded-full bg-[#FEBC2E]" />
      <span className="w-1.5 h-1.5 rounded-full bg-[#28C840]" />
      <span className="ml-3 text-[0.55rem] font-mono text-neutral-500 truncate tracking-wider">
        maisonsunday.io
      </span>
      <span className="ml-auto text-[0.45rem] font-bold tracking-[0.28em] uppercase text-neutral-500">
        Shopify Plus
      </span>
    </div>

    {/* Storefront body — sticky header + auto-scrolling page */}
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="relative h-[calc(100%-1.75rem)] overflow-hidden flex flex-col"
    >
      {/* HEADER (sticky) ─────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="shrink-0 flex items-center justify-between px-4 pt-3.5 pb-2 border-b border-black/[0.05] bg-[#F5F2EC] z-10"
      >
        <span
          style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif" }}
          className="text-[0.78rem] font-bold tracking-[0.18em] text-neutral-900 uppercase"
        >
          {STORE.brand}
        </span>
        <span className="hidden sm:flex items-center gap-3 text-[0.45rem] font-bold tracking-[0.28em] uppercase text-neutral-600">
          <span>Collection</span>
          <span>Atelier</span>
          <span>Journal</span>
        </span>
        <span className="flex items-center gap-2 text-[0.45rem] font-bold tracking-[0.28em] uppercase text-neutral-600">
          <span>Search</span>
          <span className="w-px h-2 bg-neutral-400" />
          <span>FR · EN</span>
          <span className="w-px h-2 bg-neutral-400" />
          <span>Bag · 0</span>
        </span>
      </motion.div>

      {/* SCROLL VIEWPORT — auto-pans up to reveal more sections */}
      <div className="relative flex-1 overflow-hidden">
        {/* Vertical scroll-progress rail (right edge) */}
        <div
          aria-hidden
          className="absolute top-2 bottom-2 right-1 w-px bg-black/[0.06] z-20"
        >
          <motion.span
            className="absolute left-[-1.5px] w-[4px] h-6 rounded-full bg-[#F4D64E]/70"
            initial={{ y: 0 }}
            animate={{ y: focusPdp ? 0 : ["0%", "calc(100% - 24px)"] }}
            transition={{
              duration: focusPdp ? 0.4 : 4.0,
              ease: focusPdp ? [0.22, 1, 0.36, 1] : [0.45, 0, 0.55, 1],
            }}
          />
        </div>

        {/* Tall scrolling page (animated translateY) */}
        <motion.div
          initial={{ y: 0 }}
          animate={{ y: focusPdp ? "0%" : ["0%", "-36%"] }}
          transition={{
            duration: focusPdp ? 0.45 : 4.0,
            ease: focusPdp ? [0.22, 1, 0.36, 1] : [0.45, 0, 0.55, 1],
          }}
          className="absolute inset-x-0 top-0"
        >
          {/* HERO ─────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.0, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-3 mt-3 h-[260px] rounded-sm overflow-hidden bg-neutral-200"
          >
            <img
              src={STORE.hero.img}
              alt={STORE.hero.title_a}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-black/15" />
            <span aria-hidden className="absolute inset-0 bg-gradient-to-r from-black/35 via-transparent to-black/10" />

            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.45 }}
              className="absolute top-3 left-3 inline-flex items-center gap-2 text-[0.45rem] font-bold tracking-[0.32em] uppercase text-white/95"
            >
              <span className="w-3 h-px bg-[#F4D64E]" />
              {STORE.eyebrow}
            </motion.div>

            <div className="absolute top-3 right-3 flex items-center gap-1.5 text-[0.4rem] font-bold tracking-[0.32em] uppercase text-white/70">
              <span className="text-white">01</span>
              <span className="opacity-50">·</span>
              <span className="opacity-50">02</span>
              <span className="opacity-50">·</span>
              <span className="opacity-50">03</span>
            </div>

            <div className="absolute left-3 right-3 bottom-3">
              <div
                style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif" }}
                className="text-white font-light tracking-[-0.018em] leading-[0.92]"
              >
                {[STORE.hero.title_a, STORE.hero.title_b, STORE.hero.title_c].map((t, i) => (
                  <motion.div
                    key={t}
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.5 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                    className={i === 1 ? "italic" : ""}
                    style={{ fontSize: "clamp(1.45rem, 4vw, 2.1rem)" }}
                  >
                    {t}
                  </motion.div>
                ))}
              </div>
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.95 }}
                className="mt-2.5 inline-flex items-center gap-2 px-3 py-1.5 bg-white text-neutral-900 text-[0.45rem] font-bold tracking-[0.28em] uppercase rounded-sm"
              >
                Discover the edition
                <span className="w-1 h-1 rounded-full bg-[#F4D64E]" />
              </motion.div>
            </div>
          </motion.div>

          {/* COLLECTION GRID ─────────────────────────── */}
          <div className="px-3 mt-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 1.1 }}
              className="flex items-baseline justify-between mb-2"
            >
              <span className="text-[0.45rem] font-bold tracking-[0.32em] uppercase text-neutral-700">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-3 h-px bg-[#F4D64E]" />
                  The Collection
                </span>
              </span>
              <span className="text-[0.42rem] font-bold tracking-[0.28em] uppercase text-neutral-500">
                See all ↗
              </span>
            </motion.div>

            <div className="grid grid-cols-3 gap-1.5">
              {STORE.collection.map((p, i) => (
                <motion.div
                  key={p.name}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.55,
                    delay: 1.2 + i * 0.1,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className={`relative aspect-[3/4] overflow-hidden bg-neutral-200 ring-1 ${
                    focusPdp && i === 0 ? "ring-2 ring-[#F4D64E]" : "ring-black/[0.04]"
                  } transition-all duration-500`}
                >
                  <img
                    src={p.img}
                    alt={p.name}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
                  <div className="absolute bottom-1.5 left-1.5 right-1.5">
                    <div className="text-[0.38rem] font-bold tracking-[0.32em] uppercase text-white/85">
                      {p.label}
                    </div>
                    <div
                      style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif" }}
                      className="text-[0.7rem] leading-none text-white font-light"
                    >
                      {p.name}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* EDITORIAL STORY ─────────────────────────── */}
          <div className="px-3 mt-5 grid grid-cols-12 gap-3 items-stretch">
            <div className="col-span-7 flex flex-col justify-center">
              <span className="text-[0.42rem] font-bold tracking-[0.32em] uppercase text-neutral-500 inline-flex items-center gap-1.5">
                <span className="w-3 h-px bg-[#F4D64E]" />
                {STORE.story.eyebrow}
              </span>
              <div
                style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif" }}
                className="mt-2 text-neutral-900 font-light leading-[1.05] tracking-[-0.018em]"
                // Inline font-size to keep typographic rhythm tight at small sizes
                aria-label="Editorial story title"
              >
                <span className="block" style={{ fontSize: "1.05rem" }}>{STORE.story.title_a}</span>
                <span className="block italic" style={{ fontSize: "1.05rem" }}>{STORE.story.title_b}</span>
              </div>
              <p className="mt-2 text-[0.55rem] leading-[1.55] text-neutral-700">
                {STORE.story.body}
              </p>
              <span className="mt-2 inline-flex items-center gap-1.5 text-[0.42rem] font-bold tracking-[0.28em] uppercase text-neutral-900">
                {STORE.story.cta}
                <span aria-hidden>↗</span>
              </span>
            </div>
            <div className="col-span-5 relative aspect-[4/5] bg-neutral-200 overflow-hidden rounded-sm">
              <img
                src={STORE.story.img}
                alt={STORE.story.title_a}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
              <span className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 text-[0.38rem] font-bold tracking-[0.32em] uppercase text-white/95">
                <span className="w-2 h-px bg-white/80" />
                Atelier · Paris 11
              </span>
            </div>
          </div>

          {/* PRESS STRIP ─────────────────────────────── */}
          <div className="mt-5 mx-3 py-2 border-y border-black/[0.06] flex items-center gap-3 overflow-hidden">
            <span className="shrink-0 text-[0.4rem] font-bold tracking-[0.32em] uppercase text-neutral-500">
              As seen in
            </span>
            <div className="flex items-center gap-3 overflow-hidden">
              {STORE.press.map((p, i) => (
                <span
                  key={p}
                  style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif" }}
                  className={`text-[0.65rem] font-bold tracking-[0.18em] uppercase ${
                    i % 2 === 0 ? "text-neutral-900" : "text-neutral-700"
                  } shrink-0`}
                >
                  {p}
                </span>
              ))}
            </div>
          </div>

          {/* BRAND MARK / FOOTER ─────────────────────── */}
          <div className="mt-3 px-3 pb-4 flex items-center justify-between text-[0.4rem] font-bold tracking-[0.32em] uppercase text-neutral-500">
            <span>Sunday Studio · since 2019</span>
            <span>● Free shipping over € 200</span>
          </div>
        </motion.div>
      </div>

      {/* ============================================================
          PDP PEEK overlay — appears in Convert stage. A second-tier
          surface that slides up over the storefront, focused on the
          featured Atelier coat. Stays anchored to lower portion so
          the storefront remains visible above.
      ============================================================ */}
      <AnimatePresence>
        {focusPdp ? (
          <motion.div
            key="pdp"
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0 }}
            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
            className="absolute left-0 right-0 bottom-0 h-[68%] bg-[#FAF8F3] border-t border-black/[0.07] shadow-[0_-30px_80px_-20px_rgba(0,0,0,0.30)] z-20 flex flex-col"
          >
            {/* PDP top strip */}
            <div className="flex items-center justify-between px-3 pt-2.5 pb-2 border-b border-black/[0.05]">
              <span className="inline-flex items-center gap-1.5 text-[0.42rem] font-bold tracking-[0.32em] uppercase text-[#0B0F14]">
                <span className="w-3 h-px bg-[#F4D64E]" />
                {STORE.pdp.badge}
              </span>
              <span className="text-[0.55rem] text-neutral-400">×</span>
            </div>

            {/* PDP content — split */}
            <div className="flex-1 grid grid-cols-12 gap-2 px-3 pt-2 pb-2 overflow-hidden">
              {/* Left: large editorial photo */}
              <motion.div
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="col-span-7 relative bg-neutral-200 overflow-hidden"
              >
                <img
                  src={STORE.pdp.img}
                  alt={STORE.pdp.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <span aria-hidden className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/[0.10]" />
                <div className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-1.5 text-[0.38rem] font-bold tracking-[0.32em] uppercase text-white/95">
                  <span className="w-2.5 h-px bg-white/80" />
                  Studio · 2026
                </div>
              </motion.div>

              {/* Right: details + CTA */}
              <motion.div
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.55, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
                className="col-span-5 flex flex-col"
              >
                <div className="text-[0.42rem] font-bold tracking-[0.32em] uppercase text-neutral-500">
                  {STORE.pdp.series}
                </div>
                <div
                  style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif" }}
                  className="mt-1.5 text-[1.15rem] leading-[1.05] tracking-[-0.018em] text-neutral-900 font-light"
                >
                  {STORE.pdp.name}
                </div>
                <div className="mt-1.5 h-px bg-neutral-900/15" />
                <div className="mt-1.5 text-[0.65rem] font-bold tabular-nums text-neutral-900">
                  {STORE.pdp.price}
                </div>

                {/* Size pills */}
                <div className="mt-2 flex gap-1">
                  {["XS", "S", "M", "L"].map((s, i) => (
                    <span
                      key={s}
                      className={`text-[0.4rem] font-bold tracking-[0.18em] uppercase px-1.5 py-0.5 border ${
                        i === 1 ? "border-neutral-900 text-neutral-900" : "border-neutral-300 text-neutral-500"
                      } rounded-sm`}
                    >
                      {s}
                    </span>
                  ))}
                </div>

                {/* CTA */}
                <motion.button
                  type="button"
                  aria-label="Add to bag"
                  initial={{ opacity: 0, y: 6 }}
                  animate={
                    pulseCta
                      ? { opacity: 1, y: 0, scale: 0.97, backgroundColor: "#F4D64E", color: "#0B0F14" }
                      : { opacity: 1, y: 0, scale: 1, backgroundColor: "#0B0F14", color: "#FFFFFF" }
                  }
                  transition={{ duration: 0.32, delay: 0.4 }}
                  className="mt-auto h-7 flex items-center justify-center text-[0.5rem] font-bold tracking-[0.28em] uppercase"
                >
                  Add to bag
                </motion.button>
                <div className="mt-1 text-[0.38rem] tracking-[0.18em] uppercase text-neutral-500 text-center">
                  Free shipping over € 200
                </div>
              </motion.div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* +1 burst — anchored above the CTA inside the PDP */}
      <AnimatePresence>
        {pulseCta && focusPdp ? (
          <motion.div
            key="plus"
            initial={{ opacity: 0, y: 0, scale: 0.6 }}
            animate={{ opacity: [0, 1, 1, 0], y: -22, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9 }}
            style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif" }}
            className="absolute right-7 bottom-12 text-[0.95rem] font-light tabular-nums text-neutral-900 z-30"
          >
            +1
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Cart drawer — rich, Shopify-conversion-optimized */}
      <AnimatePresence>
        {drawerOpen ? (
          <>
            {/* Subtle dim behind the drawer so it pops */}
            <motion.span
              key="drawer-scrim"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="absolute inset-0 bg-black/30 z-20"
              aria-hidden
            />
            <motion.div
              key="drawer"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="absolute top-0 right-0 bottom-0 w-[68%] bg-[#FAF8F3] border-l border-black/[0.07] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.30)] z-30 flex flex-col"
            >
              {/* Header */}
              <div className="h-7 flex items-center justify-between px-3 border-b border-black/[0.05] bg-[#F5F2EC]">
                <span
                  style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif" }}
                  className="text-[0.7rem] font-light tracking-[0.04em] text-neutral-900"
                >
                  Your bag · {STORE.cart.items.reduce((n, i) => n + i.qty, 0)}
                </span>
                <span className="text-[0.55rem] text-neutral-500">×</span>
              </div>

              {/* Free-shipping progress bar */}
              <div className="px-3 pt-2 pb-1.5">
                <div className="flex items-center justify-between text-[0.4rem] font-bold tracking-[0.28em] uppercase mb-1">
                  <span className="text-neutral-700 inline-flex items-center gap-1">
                    {STORE.cart.free_shipping_unlocked ? (
                      <>
                        <span className="text-[#197A4A]">✓</span>
                        Free shipping unlocked
                      </>
                    ) : (
                      <>Spend more, free shipping</>
                    )}
                  </span>
                  <span className="text-neutral-500">over {STORE.cart.free_shipping_threshold}</span>
                </div>
                <div className="h-1 rounded-full bg-black/[0.06] overflow-hidden">
                  <motion.span
                    className="block h-full bg-[#F4D64E] rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: STORE.cart.free_shipping_unlocked ? "100%" : "62%" }}
                    transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
                  />
                </div>
              </div>

              {/* Line items */}
              <div className="flex-1 overflow-hidden px-3">
                <div className="divide-y divide-black/[0.05]">
                  {STORE.cart.items.map((item, i) => (
                    <motion.div
                      key={item.name}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.15 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                      className="py-2 flex items-start gap-2"
                    >
                      <div
                        className="w-10 h-12 bg-cover bg-center shrink-0 rounded-sm ring-1 ring-black/[0.05]"
                        style={{ backgroundImage: `url(${item.img})` }}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-[0.4rem] font-bold tracking-[0.28em] uppercase text-neutral-500">
                          {item.meta}
                        </div>
                        <div
                          style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif" }}
                          className="mt-0.5 text-[0.78rem] leading-tight text-neutral-900 font-light truncate"
                        >
                          {item.name}
                        </div>
                        {/* Qty stepper + price */}
                        <div className="mt-1 flex items-center justify-between">
                          <div className="inline-flex items-center text-[0.45rem] font-bold tracking-[0.18em] text-neutral-700 ring-1 ring-black/[0.10] rounded-sm">
                            <span className="px-1.5 py-0.5 leading-none">−</span>
                            <span className="px-1.5 py-0.5 leading-none border-x border-black/[0.10] tabular-nums">
                              {item.qty}
                            </span>
                            <span className="px-1.5 py-0.5 leading-none">+</span>
                          </div>
                          <span className="text-[0.55rem] tabular-nums text-neutral-900 font-bold">
                            {item.price}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Stock urgency */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.5 }}
                  className="mt-1 inline-flex items-center gap-1.5 text-[0.4rem] font-bold tracking-[0.24em] uppercase text-[#B33A2F]"
                >
                  <span className="w-1 h-1 rounded-full bg-[#B33A2F] animate-pulse" />
                  {STORE.cart.stock_alert}
                </motion.div>

                {/* Upsell — Complete the look */}
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
                  className="mt-3 -mx-3 px-3 py-2 bg-[#F0EBE1] border-y border-black/[0.05]"
                >
                  <div className="flex items-baseline justify-between mb-1.5">
                    <span className="text-[0.4rem] font-bold tracking-[0.28em] uppercase text-neutral-700 inline-flex items-center gap-1.5">
                      <span className="w-3 h-px bg-[#F4D64E]" />
                      Complete the look
                    </span>
                    <span className="text-[0.38rem] font-bold tracking-[0.24em] uppercase text-neutral-500">
                      Picked for you
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {STORE.cart.upsell.map((u) => (
                      <div
                        key={u.name}
                        className="flex items-center gap-1.5 bg-[#FAF8F3] rounded-sm pr-1.5 ring-1 ring-black/[0.04]"
                      >
                        <div
                          className="w-7 h-9 bg-cover bg-center shrink-0 rounded-l-sm"
                          style={{ backgroundImage: `url(${u.img})` }}
                        />
                        <div className="min-w-0 flex-1 py-1">
                          <div
                            style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif" }}
                            className="text-[0.55rem] leading-tight text-neutral-900 font-light truncate"
                          >
                            {u.name}
                          </div>
                          <div className="text-[0.42rem] tabular-nums text-neutral-600 font-bold">
                            {u.price}
                          </div>
                        </div>
                        <button
                          type="button"
                          aria-label={`Add ${u.name}`}
                          className="shrink-0 w-5 h-5 rounded-full bg-neutral-900 text-white text-[0.6rem] leading-none flex items-center justify-center"
                        >
                          +
                        </button>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Discount applied */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.7 }}
                  className="mt-2 flex items-center justify-between"
                >
                  <span className="inline-flex items-center gap-1.5 text-[0.4rem] font-bold tracking-[0.24em] uppercase text-neutral-700">
                    <span className="px-1.5 py-0.5 bg-[#F4D64E] text-neutral-900 rounded-sm tracking-[0.18em]">
                      {STORE.cart.discount_label}
                    </span>
                    Code applied
                    <span className="text-neutral-400 text-[0.5rem]">×</span>
                  </span>
                  <span className="text-[0.5rem] font-bold tabular-nums text-[#197A4A]">
                    {STORE.cart.discount_amount}
                  </span>
                </motion.div>
              </div>

              {/* Sticky footer — totals + checkout */}
              <div className="px-3 pt-2 pb-3 border-t border-black/[0.05] bg-[#F5F2EC]">
                <div className="space-y-0.5 mb-2">
                  <div className="flex items-baseline justify-between text-[0.5rem] tracking-[0.04em] text-neutral-600">
                    <span>Subtotal</span>
                    <span className="tabular-nums">{STORE.cart.subtotal}</span>
                  </div>
                  <div className="flex items-baseline justify-between text-[0.5rem] tracking-[0.04em] text-neutral-600">
                    <span>Shipping</span>
                    <span className="tabular-nums text-[#197A4A] font-bold">FREE</span>
                  </div>
                </div>
                <div className="flex items-baseline justify-between border-t border-black/[0.06] pt-1.5">
                  <span className="text-[0.5rem] font-bold tracking-[0.28em] uppercase text-neutral-700">
                    Total
                  </span>
                  <span
                    style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif" }}
                    className="text-[1.1rem] tabular-nums text-neutral-900 font-light"
                  >
                    {STORE.cart.total}
                  </span>
                </div>

                {/* Checkout CTA */}
                <div className="mt-2 w-full h-8 bg-neutral-900 flex items-center justify-center text-[0.55rem] font-bold tracking-[0.28em] uppercase text-white rounded-sm">
                  Checkout · Secure
                  <span aria-hidden className="ml-2">→</span>
                </div>

                {/* Express checkout */}
                <div className="mt-1.5 grid grid-cols-3 gap-1">
                  <span className="h-5 rounded-sm bg-[#5A31F4] text-white text-[0.42rem] font-bold tracking-[0.05em] flex items-center justify-center">
                    shop Pay
                  </span>
                  <span className="h-5 rounded-sm bg-black text-white text-[0.42rem] font-bold tracking-[0.05em] flex items-center justify-center">
                    Pay
                  </span>
                  <span className="h-5 rounded-sm bg-[#FFC439] text-[#003087] text-[0.42rem] font-bold tracking-[0.05em] flex items-center justify-center">
                    PayPal
                  </span>
                </div>

                <div className="mt-1.5 flex items-center justify-center gap-3 text-[0.38rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
                  <span>● 30-day returns</span>
                  <span className="w-px h-2 bg-neutral-300" />
                  <span>● Atelier guarantee</span>
                </div>
              </div>
            </motion.div>
          </>
        ) : null}
      </AnimatePresence>

      {/* Order placed flourish */}
      <AnimatePresence>
        {success ? (
          <motion.div
            key="success"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="absolute inset-0 z-40 flex items-center justify-center bg-[#F5F2EC]/[0.94] backdrop-blur-sm"
          >
            <motion.div
              initial={{ y: 12, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="text-center px-6"
            >
              <div className="mx-auto inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#F4D64E]/30 ring-1 ring-[#F4D64E]">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-neutral-900">
                  <motion.path
                    d="M5 12 L10 17 L19 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                  />
                </svg>
              </div>
              <div
                style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif" }}
                className="mt-4 text-[1.3rem] leading-none tracking-[-0.01em] text-neutral-900 font-light"
              >
                Order placed.
              </div>
              <div className="mt-2 text-[0.5rem] font-bold tracking-[0.32em] uppercase text-neutral-500">
                Confirmation #SD-26-0247
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </motion.div>
  </div>
);

/* ============================================================
   Progress chip + reducer
============================================================ */
const STAGE_LABELS = {
  wireframe: "01 · Wireframe",
  code: "02 · Code",
  render: "03 · Render",
  convert: "04 · Convert",
};

const StageChip = ({ stage }) => (
  <div className="absolute top-4 left-1/2 -translate-x-1/2 z-40 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#0B0F14]/85 backdrop-blur-md border border-white/[0.10] text-[0.58rem] font-bold tracking-[0.28em] uppercase text-[#F4D64E]">
    <span className="w-1.5 h-1.5 rounded-full bg-[#F4D64E] animate-pulse" />
    <AnimatePresence mode="wait">
      <motion.span
        key={stage}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        transition={{ duration: 0.25 }}
      >
        {STAGE_LABELS[stage]}
      </motion.span>
    </AnimatePresence>
  </div>
);

const initial = { stage: "wireframe", pulseCta: false, drawerOpen: false, success: false };
const reducer = (s, a) => {
  switch (a.type) {
    case "set":
      return { ...s, stage: a.stage, pulseCta: false, drawerOpen: false, success: false };
    case "pulseCta":
      return { ...s, pulseCta: true };
    case "openDrawer":
      return { ...s, drawerOpen: true };
    case "success":
      return { ...s, success: true };
    default:
      return s;
  }
};

/* ============================================================
   Main component
============================================================ */
export default function BuildPipelineMockup() {
  const wrapperRef = useRef(null);
  const [state, dispatch] = useReducer(reducer, initial);
  const playingRef = useRef(true);

  useEffect(() => {
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      dispatch({ type: "set", stage: "render" });
      playingRef.current = false;
      return;
    }

    let timers = [];
    let alive = true;

    const runStage = (idx) => {
      if (!alive || !playingRef.current) return;
      const stage = STAGES[idx % STAGES.length];
      dispatch({ type: "set", stage });

      if (stage === "convert") {
        timers.push(setTimeout(() => dispatch({ type: "pulseCta" }), 700));
        timers.push(setTimeout(() => dispatch({ type: "openDrawer" }), 1200));
        timers.push(setTimeout(() => dispatch({ type: "success" }), 3000));
      }

      timers.push(
        setTimeout(() => runStage(idx + 1), STAGE_DURATIONS[stage])
      );
    };

    let io;
    if ("IntersectionObserver" in window && wrapperRef.current) {
      io = new IntersectionObserver(
        (entries) => {
          const inView = entries.some((e) => e.isIntersecting);
          if (inView && !playingRef.current) {
            playingRef.current = true;
            runStage(0);
          } else if (!inView) {
            playingRef.current = false;
            timers.forEach(clearTimeout);
            timers = [];
          }
        },
        { threshold: 0.25 }
      );
      io.observe(wrapperRef.current);
    }

    runStage(0);

    return () => {
      alive = false;
      timers.forEach(clearTimeout);
      if (io) io.disconnect();
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      data-testid="dev-hero-pipeline"
      className="relative w-full pt-8 sm:pt-12 md:pt-14 pb-16 md:pb-20"
      style={{ perspective: "1600px" }}
    >
      <motion.div
        className="relative mx-auto w-full max-w-[440px] lg:max-w-[480px] aspect-[4/5]"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        style={{
          transform: "rotateY(-7deg) rotateX(3deg)",
          transformStyle: "preserve-3d",
        }}
      >
        {/* Floor shadow */}
        <div
          aria-hidden
          className="absolute -bottom-12 left-[8%] right-[8%] h-12 rounded-[50%] blur-2xl bg-black/55"
        />

        {/* Frame */}
        <div
          className="relative h-full rounded-[14px] overflow-hidden border border-white/[0.10] bg-[#0F141A] shadow-[0_40px_100px_-25px_rgba(0,0,0,0.7),0_20px_40px_-15px_rgba(244,214,78,0.10)]"
          style={{ transformStyle: "preserve-3d" }}
        >
          <StageChip stage={state.stage} />

          <AnimatePresence mode="wait">
            {state.stage === "wireframe" ? (
              <motion.div
                key="wire"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="absolute inset-0"
              >
                <WireframeStage />
              </motion.div>
            ) : null}
            {state.stage === "code" ? (
              <motion.div
                key="code"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="absolute inset-0"
              >
                <CodeStage active={state.stage === "code"} />
              </motion.div>
            ) : null}
            {state.stage === "render" || state.stage === "convert" ? (
              <motion.div
                key="render"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="absolute inset-0"
              >
                <RenderStage
                  pulseCta={state.pulseCta && state.stage === "convert"}
                  drawerOpen={state.drawerOpen && state.stage === "convert"}
                  success={state.success && state.stage === "convert"}
                  focusPdp={state.stage === "convert"}
                />
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>

        {/* Ambient glow */}
        <span
          aria-hidden
          className="absolute -top-12 -right-10 w-56 h-56 rounded-full bg-[#F4D64E]/[0.18] blur-[80px] -z-10"
        />
        <span
          aria-hidden
          className="absolute -bottom-16 -left-10 w-64 h-64 rounded-full bg-[#FF7AB6]/[0.10] blur-[100px] -z-10"
        />
      </motion.div>

      {/* Stage progress dots */}
      <div className="mt-12 md:mt-16 flex items-center justify-center gap-3 flex-wrap">
        {STAGES.map((s) => {
          const active = state.stage === s;
          return (
            <div key={s} className="flex items-center gap-3">
              <span
                className={`h-px transition-all duration-500 ${
                  active ? "w-10 bg-[#F4D64E]" : "w-6 bg-white/[0.10]"
                }`}
              />
              <span
                className={`text-[0.58rem] font-bold tracking-[0.28em] uppercase tabular-nums transition-colors duration-500 ${
                  active ? "text-[#F4D64E]" : "text-neutral-600"
                }`}
              >
                {STAGE_LABELS[s]}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
